import { useMemo, useState, useRef } from "react";
import { format, isPast, isToday } from "date-fns";
import {
  Calendar,
  ListTodo,
  Plus,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useTasks,
  useUpdateTask,
  useCreateTask,
  useAddTaskAssignee,
  useAddTaskProject,
  type TaskWithRelations,
  type TaskState,
} from "@/hooks/useTasks";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import { cn } from "@/lib/utils";
import { formatProjectName } from "@/lib/format-project-name";

const STATE_ORDER: TaskState[] = ["in_progress", "todo", "backlog"];
const STATE_LABELS: Record<TaskState, string> = {
  in_progress: "In Progress",
  todo: "To Do",
  backlog: "Backlog",
  done: "Done",
};
const STATE_VARIANTS: Record<
  TaskState,
  "default" | "secondary" | "outline" | "destructive"
> = {
  in_progress: "default",
  todo: "secondary",
  backlog: "outline",
  done: "outline",
};

function TaskItem({ task }: { task: TaskWithRelations }) {
  const updateTask = useUpdateTask();
  const [expanded, setExpanded] = useState(false);
  const isOverdue =
    task.due_date &&
    isPast(new Date(task.due_date)) &&
    !isToday(new Date(task.due_date));

  const handleMarkDone = (checked: boolean) => {
    if (!checked) return;
    const previousState = task.state;
    updateTask.mutate(
      { id: task.id, state: "done" },
      {
        onSuccess: () => {
          toast("Task marked as done", {
            action: {
              label: "Undo",
              onClick: () => {
                updateTask.mutate({ id: task.id, state: previousState });
              },
            },
          });
        },
      },
    );
  };

  return (
    <div>
      <div
        className="flex items-start gap-2 p-2 rounded-md hover:bg-muted cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <Checkbox
          className="mt-0.5 shrink-0"
          checked={task.state === "done"}
          onCheckedChange={handleMarkDone}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "break-words text-sm font-medium [overflow-wrap:anywhere]",
              task.state === "done" && "line-through text-muted-foreground",
            )}
          >
            {task.title}
          </p>
          <div className="mt-1 flex min-w-0 items-center gap-2">
            {task.due_date && (
              <span
                className={cn(
                  "flex items-center gap-1 text-xs",
                  isOverdue ? "text-destructive" : "text-muted-foreground",
                )}
              >
                <Calendar className="h-3 w-3" />
                {format(new Date(task.due_date), "dd.MM.yyyy")}
              </span>
            )}
            {task.projects?.[0]?.project && (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {formatProjectName(
                  task.projects[0].project.name,
                  task.projects[0].project.event_start_date,
                )}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Select
            value={task.state}
            onValueChange={(value) => {
              updateTask.mutate({ id: task.id, state: value as TaskState });
            }}
          >
            <SelectTrigger
              className="h-6 w-auto text-xs px-2 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(["backlog", "todo", "in_progress", "done"] as TaskState[]).map(
                (s) => (
                  <SelectItem key={s} value={s} className="text-xs">
                    {STATE_LABELS[s]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="ml-8 mr-2 mb-2 p-2 rounded-md bg-muted/50 space-y-2">
          {task.description ? (
            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {task.description}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No description
            </p>
          )}
          {task.assignees.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-muted-foreground">Assignees:</span>
              {task.assignees.map((a) => (
                <Badge
                  key={a.id}
                  variant="secondary"
                  className="h-4 max-w-full text-[10px]"
                >
                  {a.person.first_name} {a.person.last_name}
                </Badge>
              ))}
            </div>
          )}
          {task.projects.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs text-muted-foreground">Projects:</span>
              {task.projects.map((p) => (
                <Badge
                  key={p.id}
                  variant="outline"
                  className="h-4 max-w-full text-[10px]"
                >
                  {formatProjectName(
                    p.project.name,
                    p.project.event_start_date,
                  )}
                </Badge>
              ))}
            </div>
          )}
          {task.due_date && (
            <div className="text-xs text-muted-foreground">
              Due: {format(new Date(task.due_date), "dd.MM.yyyy")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InlineCreateTask({
  personId,
  projectId,
}: {
  personId: string;
  projectId?: string;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const createTask = useCreateTask();
  const addAssignee = useAddTaskAssignee();
  const addProject = useAddTaskProject();

  const handleSubmit = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setIsCreating(false);
      return;
    }
    createTask.mutate(
      { title: trimmed, state: "todo" },
      {
        onSuccess: (data) => {
          addAssignee.mutate({ taskId: data.id, personId });
          if (projectId) {
            addProject.mutate({ taskId: data.id, projectId });
          }
          setTitle("");
          inputRef.current?.focus();
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      setTitle("");
      setIsCreating(false);
    }
  };

  if (!isCreating) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-xs h-7 gap-1"
        onClick={() => setIsCreating(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        New task
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1.5 px-3 py-2">
      <Input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSubmit}
        placeholder="Task title..."
        className="h-7 text-sm"
        autoFocus
      />
    </div>
  );
}

export function MyTasksPanel() {
  const { data: tasks, isLoading: tasksLoading } = useTasks();
  const { data: currentPerson, isLoading: personLoading } = useCurrentPerson();
  const { currentEntity } = useRightSidebar();
  const [showDone, setShowDone] = useState(false);

  const projectId =
    currentEntity?.type === "project" ? currentEntity.id : undefined;

  const myTasks = useMemo(() => {
    if (!tasks || !currentPerson) return [];
    return tasks.filter((task) =>
      task.assignees.some((a) => a.person?.id === currentPerson.id),
    );
  }, [tasks, currentPerson]);

  const groupedTasks = useMemo(() => {
    const groups: Record<TaskState, TaskWithRelations[]> = {
      in_progress: [],
      todo: [],
      backlog: [],
      done: [],
    };

    for (const task of myTasks) {
      groups[task.state].push(task);
    }

    return groups;
  }, [myTasks]);

  const isLoading = tasksLoading || personLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading tasks...</p>
      </div>
    );
  }

  if (!currentPerson) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <ListTodo className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">
          No person profile linked to your account
        </p>
      </div>
    );
  }

  const visibleStates = showDone
    ? [...STATE_ORDER, "done" as TaskState]
    : STATE_ORDER;
  const hasAnyTasks = visibleStates.some(
    (state) => groupedTasks[state].length > 0,
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <InlineCreateTask personId={currentPerson.id} projectId={projectId} />
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7"
          onClick={() => setShowDone(!showDone)}
        >
          {showDone ? "Hide done" : "Show done"}
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2">
          {!hasAnyTasks ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <ListTodo className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No tasks assigned to you
              </p>
            </div>
          ) : (
            visibleStates.map((state) => {
              const stateTasks = groupedTasks[state];
              if (stateTasks.length === 0) return null;

              return (
                <div key={state} className="mb-3">
                  <div className="flex items-center gap-2 px-2 py-1">
                    <Badge
                      variant={STATE_VARIANTS[state]}
                      className="text-[10px] h-5"
                    >
                      {STATE_LABELS[state]}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {stateTasks.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {stateTasks.map((task) => (
                      <TaskItem key={task.id} task={task} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
